import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from '../../../helpers/pagination-helper.ts';
import { RepairsOrderClient, RepairsQuery } from 'services/api/api-client.ts';
import {
  DXG,
  useDataGrid,
} from '../../../components/devexpress/Grid/dx-grid.ts';
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'react-router-url-params';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { useAdvancedForm } from '../../../helpers/form/useAdvancedForm.ts';
import { useWatch } from 'react-hook-form';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import { AppPagination } from '../../../components/uikit/pagination/AppPagination.tsx';
import { Filters } from './components/Filters.tsx';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import {
  abasUnavailableUrn,
  ErrObj,
  formatDate,
} from '../../../helpers/abas-helper.ts';
import { AbasPlaceholder } from '../../../components/uikit/AbasPlaceholder.tsx';
import clsx from 'clsx';
import {
  DataGridRef,
  IColumnProps,
  Pager,
  Scrolling,
} from 'devextreme-react/data-grid';
import styles from './Repairs.module.scss';
import formStyles from '../../../components/form/form.module.scss';
import { ActionText } from '../../../components/uikit/ActionText.tsx';
import ArrowRight from 'assets/icons/arrow-long-right.svg?react';
import gridStyles from '../../../components/devexpress/Grid/grid.module.scss';
import {
  onContextMenuPreparing,
  TableHeaderCell,
} from '../../../helpers/tableHeaderCell/TableHeaderCell.tsx';
import { headerSortClick } from '../../../helpers/sort-helper.ts';
import {
  Button,
  ButtonColor,
} from '../../../components/uikit/buttons/Button.tsx';
import { Links } from '../../../application/constants/links.ts';
import { FeatureFlags } from '../../../application/constants/env-variables.ts';
import { Tip } from '../../../components/uikit/Tip.tsx';
import {
  getStatus,
  getStatusValues,
  mapStatusQueryParamToForm,
} from './components/statuses.ts';
import { MultiPdfModal } from './components/MultiPDFModal.tsx';
import { Tooltip } from '../../../components/uikit/Tooltip.tsx';

export type PaginationProps = {
  page: number;
  perPage: number;
  totalCount: number;
  pageSizes: number[];
  changePagination: (pagination: { page?: number; perPage?: number }) => void;
};

export type FormStatus = {
  value: string;
  label: string;
};

export type FormParams = {
  incomingNumber: string;
  serialNumber: string;
  selectedStatuses: FormStatus[];
};

const pageSizes = [10, 20, 50];

export const Repairs = () => {
  const i18n = useScopedTranslation('Page.Repairs');

  const [isMultiPDFModalVisible, setIsMultiPDFModalVisible] = useState(false);
  const [isPDFGenerating, setIsPDFGenerating] = useState(false);
  const [pdfInputValue, setPdfInputValue] = useState('');
  const [isErrorTooltipVisible, setIsErrorTooltipVisible] = useState(false);

  const changeMultiPDFModalVisibility = (value: boolean) => {
    setIsMultiPDFModalVisible(value);
  };

  const changePDFInputValue = (value: string) => {
    setPdfInputValue(value);
  };

  const dataGridRef = useRef<DataGridRef<any, number>>(null);
  const navigate = useNavigate();

  useDataGrid();
  const [params, setParams] = useQueryParams({
    incomingNumber: StringParam,
    serialNumber: StringParam,
    selectedStatuses: StringParam,
    ...pagingSortingQueryParams(pageSizes[0]),
    sortBy: withDefault(StringParam, 'order'),
    desc: withDefault(BooleanParam, true),
  });

  const selectedStatuses = (params.selectedStatuses ?? '')
    .split('_')
    .map((x) => x.trim())
    .filter(Boolean);

  const repairsQuery = RepairsQuery.useGetQuery(
    {
      incomingNumber: params.incomingNumber ?? '',
      serialNumber: params.serialNumber ?? '',
      selectedStatuses,
      ...pagingSortingToBackendRequest(params),
    },
    {
      throwOnError: false,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );

  const ordersForPDF = RepairsQuery.useGetRepairOrdersForPdfGenerationQuery(
    pdfInputValue,
    {
      throwOnError: false,
      refetchInterval: false,
    },
  );

  const generatePDF = async (incomingNumbers: string[]) => {
    try {
      setIsPDFGenerating(true);

      const pdfInfo = await RepairsOrderClient.printRepairOrder(
        incomingNumbers,
      );

      const url = URL.createObjectURL(pdfInfo.data);
      window.open(url, '_blank');
    } catch (e) {
      setIsErrorTooltipVisible(true);
      setTimeout(() => setIsErrorTooltipVisible(false), 4000);
    } finally {
      setIsPDFGenerating(false);
    }
  };

  const form = useAdvancedForm<FormParams>(
    async (data) => {
      setParams({
        ...params,
        incomingNumber: data.incomingNumber,
        serialNumber: data.serialNumber,
        selectedStatuses: getStatusValues(data.selectedStatuses),
        page: 1,
      });
    },
    {
      defaultValues: {
        incomingNumber: params.incomingNumber ?? '',
        serialNumber: params.serialNumber ?? '',
        selectedStatuses: mapStatusQueryParamToForm(params.selectedStatuses),
        ...pagingSortingToBackendRequest(params),
      },
    },
  );

  const [serialNumber, incomingNumber] = useWatch({
    control: form.control,
    name: ['serialNumber', 'incomingNumber'],
  });

  const flatData = repairsQuery.data?.records ?? [];

  const changePagination = (pagination: {
    page?: number;
    perPage?: number;
  }) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...(pagination.page !== undefined && { page: pagination.page }),
      ...(pagination.perPage !== undefined && { perPage: pagination.perPage }),
    }));
  };

  const paginationProps: PaginationProps = useMemo(() => {
    return {
      page: params.page,
      perPage: params.perPage,
      totalCount: repairsQuery?.data?.countOfRecords ?? 0,
      pageSizes: pageSizes,
      changePagination: changePagination,
    };
  }, [params, repairsQuery?.data?.countOfRecords]);

  const resetFilters = () => {
    form.reset({
      incomingNumber: '',
      serialNumber: '',
      selectedStatuses: [],
    });

    void form.handleSubmitDefault();
  };

  const changeSorting = (sortBy: string) => {
    setParams((prevParams) => ({
      ...prevParams,
      sortBy,
      desc: prevParams.sortBy !== sortBy ? false : !prevParams.desc,
      page: 1,
    }));
  };

  const columnsConfig = getColumnsConfig(i18n);

  const isResetButtonShow =
    !!params.serialNumber ||
    !!params.incomingNumber ||
    !!selectedStatuses.length;

  const filterProps = {
    form,
    incomingNumber,
    serialNumber,
    resetFilters,
    isResetButtonShow,
  };

  const errObj = repairsQuery?.error as ErrObj;
  const isExternalServiceUnavailable =
    errObj?.response?.type === abasUnavailableUrn;

  const isPlaceholderVisible =
    !repairsQuery.isLoading &&
    !repairsQuery.isPlaceholderData &&
    !flatData.length &&
    !isExternalServiceUnavailable;

  const isDataEmpty =
    ordersForPDF.isSuccess &&
    ordersForPDF?.data.countOfRecords === 0 &&
    !pdfInputValue;

  return (
    <div data-test-id={'repairs-container'}>
      <Breadcrumbs>
        <div className={clsx(styles.rowContainer, styles.columnGap16)}>
          {FeatureFlags.isPDFGeneratingEnabled() && (
            <Button
              title={i18n.t('generate_multi_pdf')}
              color={ButtonColor.Secondary}
              onClick={() => changeMultiPDFModalVisibility(true)}
            />
          )}
          {FeatureFlags.isCreateRepairRequestEnabled() && (
            <Button
              title={i18n.t('create_repair_requests')}
              color={ButtonColor.Primary}
              onClick={() =>
                navigate(Links.Authorized.CreateRepairRequest.route)
              }
              testId={'create-repair-request-button'}
            />
          )}
        </div>
      </Breadcrumbs>
      <Loading
        loading={
          repairsQuery.isLoading ||
          repairsQuery.isPlaceholderData ||
          isPDFGenerating
        }
        title={isPDFGenerating ? i18n.t('generating_pdf') : undefined}
        textClassName={styles.loaderText}
      >
        <Filters {...filterProps} />
        {!isExternalServiceUnavailable && (
          <DXG.DataGrid
            ref={dataGridRef}
            dataSource={flatData}
            className={clsx(
              gridStyles.editorOutline,
              formStyles.table,
              styles.fix,
              !flatData.length && styles.borderBottom0,
            )}
            sorting={{ mode: 'none' }}
            paging={{ pageSize: flatData.length }}
            noDataText={''}
            onContextMenuPreparing={onContextMenuPreparing}
            onCellClick={(event) => headerSortClick(event, changeSorting)}
          >
            <Scrolling
              columnRenderingMode={'virtual'}
              showScrollbar={'always'}
            />
            {columnsConfig.map((x, index) => {
              return (
                <DXG.Column
                  cssClass={clsx(x.cssClass, formStyles.text)}
                  key={`${x.dataField}.${index}`}
                  showEditorAlways={x.allowEditing}
                  headerCellRender={(cellInfo) => (
                    <TableHeaderCell
                      dataField={cellInfo.column.dataField}
                      caption={cellInfo.column.caption}
                      sortBy={params.sortBy}
                      desc={params.desc}
                    />
                  )}
                  {...x}
                />
              );
            })}
            <Pager visible={false} />
          </DXG.DataGrid>
        )}
        <MultiPdfModal
          isModalVisible={isMultiPDFModalVisible}
          changeMultiPDFModalVisibility={changeMultiPDFModalVisibility}
          changePDFInputValue={changePDFInputValue}
          isLoading={ordersForPDF.isFetching}
          data={ordersForPDF.data}
          generatePDF={generatePDF}
          isDataEmpty={isDataEmpty}
        />
        {isExternalServiceUnavailable && (
          <AbasPlaceholder containerClassname={styles.marginTop152} />
        )}
        {isPlaceholderVisible && <TablePlaceholder />}
        {isErrorTooltipVisible && (
          <Tooltip
            closeTooltip={() => setIsErrorTooltipVisible(false)}
            bodyContent={<div>{i18n.t('error_popup')}</div>}
            className={styles.errorTooltip}
            iconClassName={styles.errorTooltipIcon}
            showCancelButton={true}
            isError={true}
          />
        )}
        <AppPagination {...paginationProps} />
      </Loading>
    </div>
  );
};

const getColumnsConfig = (
  i18n: any,
): (IColumnProps & { disableExport?: boolean })[] => [
  {
    dataField: 'order',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('incoming_number'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 240,
  },
  {
    dataField: 'sn',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('sn'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 275,
  },
  {
    dataField: 'from',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('from'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 275,
    calculateCellValue: (value) => formatDate(value.from),
  },
  {
    dataField: 'status',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('status'),
    cssClass: clsx(
      formStyles.textAlignLeft,
      formStyles.textCellPadding,
      formStyles.tableHeaderPointer,
      styles.statusCellPadding,
    ),
    minWidth: 480,
    cellRender: (cell) => {
      const statusData = getStatus(cell.value);

      if (statusData) {
        return (
          <Tip
            tooltipText={i18n.t(statusData.key)}
            textBody={
              <div
                data-test-id={statusData.testValue}
                className={clsx(styles.status, statusData.className)}
              >
                <div className={styles.statusIconDiv}>
                  <statusData.Icon />
                </div>
                <div className={styles.statusText}>
                  {i18n.t(statusData.key)}
                </div>
              </div>
            }
            className={styles.tooltip}
            tooltipClassName={styles.tipMargins}
          />
        );
      }

      return <div className={styles.notFoundStatusPadding}>{cell.value}</div>;
    },
  },
  {
    dataField: '',
    caption: '',
    cssClass: clsx(formStyles.textCellPadding, styles.buttonPadding),
    width: 130,
    cellRender: (cell) => {
      const url =
        window.location.origin +
        window.location.pathname +
        '/' +
        cell.data.order;

      return (
        <Link
          to={url}
          className={styles.openButton}
          data-test-id={'detailed-info-link'}
        >
          <ActionText text={i18n.t('detailed_info')} />
          <ArrowRight />
        </Link>
      );
    },
  },
];
